import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { combineLatest, EMPTY, filter, finalize, Subscription, switchMap, take } from 'rxjs';
import { PendingProductionManagerApproval } from 'src/app/core/interfaces/production/pending-production-manager-approval';
import { ApiService } from 'src/app/core/services/api/api.service';
import { RoleService } from 'src/app/core/services/role/role.service';
import { FeatureFlagService } from 'src/app/core/services/feature-flags/feature-flag.service';
import { SnackbarService } from 'src/app/core/services/snackbar/snackbar.service';

@Component({
  selector: 'app-pending-production-manager-approval-list',
  templateUrl: './pending-production-manager-approval-list.component.html',
  styleUrls: ['./pending-production-manager-approval-list.component.scss'],
})
export class PendingProductionManagerApprovalListComponent implements OnInit, AfterViewInit, OnDestroy {
  displayedColumns: string[] = [
    'contractId', 'productionSchedulingNotes', 'projectTypes', 'customerName',
    'jobAddress', 'soldDate', 'contractApprovalDate', 'contractTotal', 'zoneOffice',
  ];
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  dataSource = new MatTableDataSource<PendingProductionManagerApproval>([]);
  isLoading = true;
  errorMessage = '';
  private queueSubscription?: Subscription;

  constructor(
    private api: ApiService,
    private router: Router,
    private role: RoleService,
    private featureFlags: FeatureFlagService,
    private snackbar: SnackbarService
  ) {}

  ngOnInit(): void {
    // RoleService publishes the resolved role before its offline status.
    // Ignore the initial offline=false until that role has been resolved,
    // then consume the status emission that completes its classification.
    this.queueSubscription = combineLatest([this.role.currentOfflineStatus, this.featureFlags.resolved$])
      .pipe(
        filter(([, featureState]) => !!this.role.getCurrentUserRole() && featureState.status !== 'loading'),
        take(1),
        switchMap(([offline, featureState]) => {
          if (featureState.status !== 'resolved' || !this.featureFlags.isEnabled('PendingProductionManagerApproval')) {
            this.errorMessage = 'Pending Production Manager Approval is disabled for the selected company.';
            this.snackbar.openErrorSnackBar(this.errorMessage);
            this.router.navigate(['/dashboard']);
            return EMPTY;
          }
          if (offline) {
            this.errorMessage = 'Pending Production Manager Approval is available in online mode.';
            return EMPTY;
          }
          return this.api.getPendingProductionManagerApprovals();
        }),
        finalize(() => this.isLoading = false),
      )
      .subscribe({
        next: rows => this.dataSource.data = rows,
        error: () => this.errorMessage = 'Unable to load pending contracts. Please refresh the page to try again.',
      });
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  filterContracts(event: Event): void {
    this.dataSource.filter = (event.target as HTMLInputElement).value.trim().toLowerCase();
    this.paginator?.firstPage();
  }

  goToContract(row: PendingProductionManagerApproval): void {
    this.router.navigate(['customers/' + row.customerId], {
      queryParams: {
        jobAddressId: row.jobAddressId,
        contractId: row.contractId,
        pendingProductionManagerApproval: true,
      },
    });
  }

  ngOnDestroy(): void {
    this.queueSubscription?.unsubscribe();
  }
}
